// 收集系统 - 植物收集、物品收集、图鉴
import { GameState } from './GameState.js';
import { FloatingTextSystem } from './FloatingText.js';

// 收集品类型
export enum CollectionCategory {
  PLANT = 'plant',      // 植物
  ITEM = 'item',        // 物品
  INSECT = 'insect',    // 昆虫
  MINERAL = 'mineral',  // 矿物
  FOSSIL = 'fossil',    // 化石
  CRAFT = 'craft'       // 手工
}

// 类型别名
export type CollectionType = CollectionCategory;

// 收集分类常量
export const COLLECTION_CATEGORIES = {
  PLANT: CollectionCategory.PLANT,
  ITEM: CollectionCategory.ITEM,
  INSECT: CollectionCategory.INSECT,
  MINERAL: CollectionCategory.MINERAL,
  FOSSIL: CollectionCategory.FOSSIL,
  CRAFT: CollectionCategory.CRAFT,
} as const;

// 收集品稀有度
export enum Rarity {
  COMMON = 'common',      // 普通
  UNCOMMON = 'uncommon',  // 稀有
  RARE = 'rare',          // 罕见
  EPIC = 'epic',          // 史诗
  LEGENDARY = 'legendary' // 传说
}

// 收集品数据
export interface Collectible {
  id: string;
  name: string;
  description: string;
  category: CollectionCategory;
  rarity: Rarity;
  emoji: string;
  // 发现条件
  unlockCondition?: {
    weather?: string;
    season?: string;
    time?: string;
    location?: string;
    quest?: string;
    friendship?: { npc: string; level: number };
  };
  // 出现概率
  spawnChance: number;
  // 价值
  value: number;
  // 是否可重复收集
  repeatable: boolean;
  // 收集次数
  collectCount?: number;
  // 图鉴描述
  lore: string;
  // 趣味小知识
  trivia: string;
}

// 植物收集品
export const PLANT_COLLECTION: Collectible[] = [
  {
    id: 'dandelion',
    name: '蒲公英',
    description: '随风飘散的白色绒毛',
    category: CollectionCategory.PLANT,
    rarity: Rarity.COMMON,
    emoji: '🌼',
    unlockCondition: { season: 'spring' },
    spawnChance: 0.3,
    value: 10,
    repeatable: true,
    lore: '农村孩子最喜欢吹蒲公英的绒毛，看它们随风飘散。',
    trivia: '蒲公英的绒毛其实是它的种子，可以飞到很远的地方生根发芽。'
  },
  {
    id: 'sunflower',
    name: '向日葵',
    description: '永远向着太阳的大黄花',
    category: CollectionCategory.PLANT,
    rarity: Rarity.COMMON,
    emoji: '🌻',
    unlockCondition: { season: 'summer' },
    spawnChance: 0.2,
    value: 20,
    repeatable: true,
    lore: '奶奶种了很多向日葵，瓜子炒着吃特别香。',
    trivia: '向日葵的花盘会随着太阳的移动而转动，这叫向光性。'
  },
  {
    id: 'morning_glory',
    name: '牵牛花',
    description: '清晨开放的紫色小花',
    category: CollectionCategory.PLANT,
    rarity: Rarity.UNCOMMON,
    emoji: '🌸',
    unlockCondition: { time: 'morning' },
    spawnChance: 0.15,
    value: 30,
    repeatable: true,
    lore: '篱笆上的牵牛花，大清早就开了，像一个个小喇叭。',
    trivia: '牵牛花也叫喇叭花，只在早晨开放，太阳大了就谢了。'
  },
  {
    id: 'bamboo',
    name: '竹子',
    description: '挺拔青翠的竹子',
    category: CollectionCategory.PLANT,
    rarity: Rarity.COMMON,
    emoji: '🎋',
    spawnChance: 0.25,
    value: 25,
    repeatable: true,
    lore: '竹林是孩子们玩耍的好地方，夏天特别凉快。',
    trivia: '竹子是世界上生长最快的植物，一天能长一米多高！'
  },
  {
    id: 'mushroom',
    name: '野蘑菇',
    description: '雨后冒出来的小伞',
    category: CollectionCategory.PLANT,
    rarity: Rarity.UNCOMMON,
    emoji: '🍄',
    unlockCondition: { weather: 'rainy' },
    spawnChance: 0.2,
    value: 40,
    repeatable: true,
    lore: '雨后树林里会长出各种蘑菇，奶奶认得哪些能吃。',
    trivia: '有些蘑菇有毒，千万不能随便采来吃！'
  },
  {
    id: 'cactus_flower',
    name: '仙人掌花',
    description: '沙漠植物开出的美丽花朵',
    category: CollectionCategory.PLANT,
    rarity: Rarity.RARE,
    emoji: '🌵',
    unlockCondition: { season: 'summer', time: 'noon' },
    spawnChance: 0.05,
    value: 100,
    repeatable: false,
    lore: '张姐姐从城里带回来的仙人掌，居然开花了！',
    trivia: '仙人掌开花很罕见，有些品种要等很多年才会开一次花。'
  },
  {
    id: 'four_leaf_clover',
    name: '四叶草',
    description: '传说能带来幸运的三叶草变种',
    category: CollectionCategory.PLANT,
    rarity: Rarity.RARE,
    emoji: '🍀',
    spawnChance: 0.02,
    value: 200,
    repeatable: false,
    lore: '找到了四叶草的人会有好运气！',
    trivia: '大约一万朵三叶草中才会有一朵四叶草。'
  },
  {
    id: 'ginseng',
    name: '人参',
    description: '珍贵的药材，形似人形',
    category: CollectionCategory.PLANT,
    rarity: Rarity.EPIC,
    emoji: '🌿',
    unlockCondition: { friendship: { npc: 'grandma', level: 50 } },
    spawnChance: 0.01,
    value: 500,
    repeatable: false,
    lore: '放牛山上有人参，但只有懂行的采药人才能找到。',
    trivia: '野生人参非常珍贵，被称为"百草之王"。'
  },
  {
    id: 'golden_lotus',
    name: '金莲',
    description: '传说中的金色莲花',
    category: CollectionCategory.PLANT,
    rarity: Rarity.LEGENDARY,
    emoji: '🪷',
    unlockCondition: { quest: 'legendary_quest' },
    spawnChance: 0.001,
    value: 2000,
    repeatable: false,
    lore: '传说放牛山深处有一株千年金莲，见到的人会有好运气。',
    trivia: '金莲是佛教中的圣花，象征纯洁和神圣。'
  },
  {
    id: 'lotus',
    name: '荷花',
    description: '出淤泥而不染',
    category: CollectionCategory.PLANT,
    rarity: Rarity.RARE,
    emoji: '🪷',
    unlockCondition: { season: 'summer', location: 'pond' },
    spawnChance: 0.05,
    value: 150,
    repeatable: true,
    lore: '村口池塘里的荷花夏天开得很美。',
    trivia: '荷花全身都是宝，莲藕、莲子都能吃。'
  },
  {
    id: 'lotus_leaf',
    name: '莲叶',
    description: '大雨点落在上面会像珍珠一样滚动',
    category: CollectionCategory.PLANT,
    rarity: Rarity.UNCOMMON,
    emoji: '🍃',
    unlockCondition: { season: 'summer', location: 'pond' },
    spawnChance: 0.1,
    value: 30,
    repeatable: true,
    lore: '可以用来包荷叶饭，特别香。',
    trivia: '莲叶的表面有纳米结构，水珠在上面会滚来滚去。'
  },
  {
    id: 'reed',
    name: '芦苇',
    description: '秋天会飘出白色的芦花',
    category: CollectionCategory.PLANT,
    rarity: Rarity.COMMON,
    emoji: '🌿',
    unlockCondition: { location: 'pond' },
    spawnChance: 0.25,
    value: 15,
    repeatable: true,
    lore: '河边的芦苇丛是孩子们的乐园。',
    trivia: '芦苇可以用来编草帽、造纸。'
  },
  {
    id: 'water_lily',
    name: '睡莲',
    description: '浮在水面上的美丽小花',
    category: CollectionCategory.PLANT,
    rarity: Rarity.RARE,
    emoji: '🪴',
    unlockCondition: { season: 'summer', location: 'pond' },
    spawnChance: 0.08,
    value: 100,
    repeatable: true,
    lore: '睡莲白天开花晚上就合上了。',
    trivia: '睡莲不是莲花，它们的科属不同。'
  }
];

// 物品收集品
export const ITEM_COLLECTION: Collectible[] = [
  {
    id: 'old_coin',
    name: '古钱币',
    description: '一枚生锈的铜钱',
    category: CollectionCategory.ITEM,
    rarity: Rarity.COMMON,
    emoji: '🪙',
    spawnChance: 0.2,
    value: 50,
    repeatable: true,
    lore: '在院子里挖土时经常能挖到古钱币，爷爷说是以前的人掉的。',
    trivia: '中国古代的铜钱中间有个方孔，可以用绳子串起来。'
  },
  {
    id: 'clay_marble',
    name: '泥弹珠',
    description: '用泥土搓成的玩具',
    category: CollectionCategory.ITEM,
    rarity: Rarity.COMMON,
    emoji: '🔵',
    spawnChance: 0.3,
    value: 5,
    repeatable: true,
    lore: '孩子们最喜欢的玩具，把泥巴搓成球晒干就能玩。',
    trivia: '以前的玩具都是自制的，不需要花钱。'
  },
  {
    id: 'bamboo_dragonfly',
    name: '竹蜻蜓',
    description: '用竹片做的飞行器',
    category: CollectionCategory.ITEM,
    rarity: Rarity.UNCOMMON,
    emoji: '🚁',
    spawnChance: 0.15,
    value: 30,
    repeatable: true,
    lore: '王大爷的手艺，搓一下就能飞上天。',
    trivia: '竹蜻蜓是中国古代的发明，据说启发了直升机的诞生。'
  },
  {
    id: 'kite',
    name: '风筝',
    description: '用纸和竹条做的玩具',
    category: CollectionCategory.ITEM,
    rarity: Rarity.UNCOMMON,
    emoji: '🪁',
    unlockCondition: { season: 'spring', weather: 'windy' },
    spawnChance: 0.1,
    value: 60,
    repeatable: true,
    lore: '春天放风筝是孩子们最期待的活动。',
    trivia: '风筝最早是中国发明的，已经有两千多年历史了。'
  },
  {
    id: 'old_photo',
    name: '老照片',
    description: '泛黄的黑白照片',
    category: CollectionCategory.ITEM,
    rarity: Rarity.RARE,
    emoji: '📷',
    unlockCondition: { friendship: { npc: 'grandma', level: 30 } },
    spawnChance: 0.08,
    value: 150,
    repeatable: false,
    lore: '照片里是年轻时的爷爷奶奶，他们那时候真好看。',
    trivia: '以前拍照要去照相馆，很贵，所以照片都很珍惜。'
  },
  {
    id: 'letter',
    name: '旧信件',
    description: '一封泛黄的信',
    category: CollectionCategory.ITEM,
    rarity: Rarity.RARE,
    emoji: '💌',
    spawnChance: 0.05,
    value: 200,
    repeatable: false,
    lore: '字迹已经模糊了，但能看出是爷爷写给奶奶的。',
    trivia: '以前没有手机，恋人之间要靠写信联系，这叫"情书"。'
  },
  {
    id: 'jade_pendant',
    name: '玉佩',
    description: '雕刻精美的玉饰',
    category: CollectionCategory.ITEM,
    rarity: Rarity.EPIC,
    emoji: '💎',
    unlockCondition: { quest: 'family_legacy' },
    spawnChance: 0.03,
    value: 800,
    repeatable: false,
    lore: '奶奶说是祖上传下来的，要代代相传。',
    trivia: '中国人认为玉能辟邪保平安，是重要的传家宝。'
  },
  {
    id: 'bronze_mirror',
    name: '铜镜',
    description: '古旧的梳妆镜',
    category: CollectionCategory.ITEM,
    rarity: Rarity.EPIC,
    emoji: '🪞',
    spawnChance: 0.02,
    value: 1000,
    repeatable: false,
    lore: '奶奶年轻时用的镜子，背面还有花纹。',
    trivia: '古代没有玻璃镜子，用的是打磨光滑的铜镜。'
  }
];

// 昆虫收集品
export const INSECT_COLLECTION: Collectible[] = [
  {
    id: 'butterfly',
    name: '蝴蝶',
    description: '翅膀上有美丽花纹',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🦋',
    unlockCondition: { season: 'spring' },
    spawnChance: 0.25,
    value: 15,
    repeatable: true,
    lore: '花丛中飞舞的蝴蝶，孩子们喜欢追着跑。',
    trivia: '蝴蝶的寿命很短，大多数只能活几个星期。'
  },
  {
    id: 'dragonfly',
    name: '蜻蜓',
    description: '飞得很快的昆虫',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🦟',
    unlockCondition: { season: 'summer' },
    spawnChance: 0.3,
    value: 10,
    repeatable: true,
    lore: '夏天有很多蜻蜓，低飞的时候说明要下雨了。',
    trivia: '蜻蜓是捉蚊子的高手，一只蜻蜓一天能吃几百只蚊子。'
  },
  {
    id: 'cricket',
    name: '蟋蟀',
    description: '会唱歌的小虫',
    category: CollectionCategory.INSECT,
    rarity: Rarity.UNCOMMON,
    emoji: '🦗',
    unlockCondition: { time: 'night' },
    spawnChance: 0.15,
    value: 35,
    repeatable: true,
    lore: '秋天的夜晚，蟋蟀的叫声特别响亮。',
    trivia: '古代人喜欢斗蟋蟀，把它们放在一起看谁叫得大声。'
  },
  {
    id: 'firefly',
    name: '萤火虫',
    description: '会发光的昆虫',
    category: CollectionCategory.INSECT,
    rarity: Rarity.RARE,
    emoji: '✨',
    unlockCondition: { season: 'summer', time: 'night' },
    spawnChance: 0.08,
    value: 80,
    repeatable: true,
    lore: '夏夜的河边，萤火虫一闪一闪的，像星星落在地上。',
    trivia: '萤火虫发光是为了寻找配偶，只有成虫才会发光。'
  },
  {
    id: 'mantis',
    name: '螳螂',
    description: '举着大刀的昆虫',
    category: CollectionCategory.INSECT,
    rarity: Rarity.UNCOMMON,
    emoji: '🦗',
    spawnChance: 0.1,
    value: 40,
    repeatable: true,
    lore: '螳螂的前腿像镰刀，看起来很威风。',
    trivia: '螳螂是益虫，会吃害虫保护庄稼。'
  },
  {
    id: 'cicada',
    name: '蝉',
    description: '夏天叫个不停地知了',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🐦',
    unlockCondition: { season: 'summer' },
    spawnChance: 0.25,
    value: 15,
    repeatable: true,
    lore: '一到夏天，知了的叫声就不停。',
    trivia: '蝉要在地下待很多年才能钻出来羽化。'
  },
  {
    id: 'dragonfly_blue',
    name: '蓝蜻蜓',
    description: '蓝色的蜻蜓',
    category: CollectionCategory.INSECT,
    rarity: Rarity.UNCOMMON,
    emoji: '🪰',
    spawnChance: 0.12,
    value: 35,
    repeatable: true,
    lore: '蓝蜻蜓飞得很快，很难抓到。',
    trivia: '蜻蜓是飞行高手，能在空中悬停和倒飞。'
  },
  {
    id: 'butterfly_yellow',
    name: '黄蝴蝶',
    description: '黄色的蝴蝶',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🦋',
    unlockCondition: { season: 'spring' },
    spawnChance: 0.2,
    value: 20,
    repeatable: true,
    lore: '春天的花丛中有很多蝴蝶飞舞。',
    trivia: '蝴蝶的翅膀上有鳞片，能反射阳光产生美丽的颜色。'
  },
  {
    id: 'beetle',
    name: '甲虫',
    description: '硬壳的小虫子',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🪲',
    spawnChance: 0.25,
    value: 10,
    repeatable: true,
    lore: '树根下经常能挖到甲虫。',
    trivia: '甲虫是地球上种类最多的动物。'
  },
  {
    id: 'ladybug',
    name: '瓢虫',
    description: '红色的背上有黑点',
    category: CollectionCategory.INSECT,
    rarity: Rarity.UNCOMMON,
    emoji: '🐞',
    spawnChance: 0.15,
    value: 25,
    repeatable: true,
    lore: '瓢虫是农民的好朋友，会吃蚜虫。',
    trivia: '瓢虫背上的点数代表不同的种类。'
  },
  {
    id: 'bee',
    name: '蜜蜂',
    description: '勤快的采蜜工',
    category: CollectionCategory.INSECT,
    rarity: Rarity.UNCOMMON,
    emoji: '🐝',
    spawnChance: 0.18,
    value: 30,
    repeatable: true,
    lore: '花丛中经常能看到蜜蜂忙来忙去。',
    trivia: '蜜蜂要采1000朵花才能酿出一滴蜂蜜。'
  },
  {
    id: 'wasp',
    name: '马蜂',
    description: '会蜇人的蜂',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🐛',
    spawnChance: 0.2,
    value: 15,
    repeatable: true,
    lore: '屋檐下经常有马蜂窝，要小心。',
    trivia: '马蜂一般不主动攻击人，除非你靠近它的窝。'
  },
  {
    id: 'snail',
    name: '蜗牛',
    description: '背着房子的小虫',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🐌',
    unlockCondition: { weather: 'rain' },
    spawnChance: 0.3,
    value: 8,
    repeatable: true,
    lore: '下雨后蜗牛就会爬出来。',
    trivia: '蜗牛是世界上牙齿最多的动物，有26000多颗！'
  },
  {
    id: 'grasshopper',
    name: '蚂蚱',
    description: '会跳的绿色小虫',
    category: CollectionCategory.INSECT,
    rarity: Rarity.COMMON,
    emoji: '🦗',
    spawnChance: 0.25,
    value: 12,
    repeatable: true,
    lore: '草丛里到处都能看到蚂蚱跳来跳去。',
    trivia: '蚂蚱的后腿特别发达，能跳比自己身体长几十倍的距离。'
  }
];

// 矿物收集品
export const MINERAL_COLLECTION: Collectible[] = [
  {
    id: 'smooth_stone',
    name: '鹅卵石',
    description: '河边捡到的圆石头',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.COMMON,
    emoji: '🪨',
    spawnChance: 0.3,
    value: 5,
    repeatable: true,
    lore: '河边有很多被水冲刷得很光滑的石头。',
    trivia: '鹅卵石要经过很多年河水的冲刷才能变得这么光滑。'
  },
  {
    id: 'quartz',
    name: '水晶',
    description: '透明的六棱柱',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.UNCOMMON,
    emoji: '💎',
    spawnChance: 0.1,
    value: 60,
    repeatable: true,
    lore: '放牛山的石头缝里有时能找到亮晶晶的水晶。',
    trivia: '水晶其实是石英，是地壳里最常见的矿物之一。'
  },
  {
    id: 'agate',
    name: '玛瑙',
    description: '有漂亮花纹的石头',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.RARE,
    emoji: '🟤',
    spawnChance: 0.05,
    value: 150,
    repeatable: false,
    lore: '石头上有像云彩一样的花纹，奶奶说这叫"天画"。',
    trivia: '玛瑙是一种玉石，古人认为它能辟邪。'
  },
  {
    id: 'meteorite',
    name: '陨石',
    description: '从天上落下来的石头',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.LEGENDARY,
    emoji: '☄️',
    unlockCondition: { time: 'night' },
    spawnChance: 0.005,
    value: 3000,
    repeatable: false,
    lore: '听说是流星掉下来的石头，非常罕见。',
    trivia: '陨石来自外太空，是研究宇宙的重要材料。'
  },
  // 新增70/80年代矿物
  {
    id: 'iron_stone',
    name: '铁矿石',
    description: '含有铁的石头，可以冶炼',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.COMMON,
    emoji: '🪨',
    spawnChance: 0.2,
    value: 10,
    repeatable: true,
    lore: '山上的石头里经常能看到锈红色的铁矿石。',
    trivia: '铁矿石是炼铁的主要原料，人类使用铁器已经有几千年历史了。'
  },
  {
    id: 'coal',
    name: '煤块',
    description: '乌黑发亮的煤块',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.COMMON,
    emoji: '⚫',
    spawnChance: 0.15,
    value: 15,
    repeatable: true,
    lore: '可以用来生火，冬天取暖最好用了。',
    trivia: '煤是古代植物埋在地下经过几亿年形成的化石燃料。'
  },
  {
    id: 'clay_lump',
    name: '黏土块',
    description: '可以制作陶器的黏土',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.COMMON,
    emoji: '🟫',
    spawnChance: 0.25,
    value: 5,
    repeatable: true,
    lore: '河边挖的黏土，可以捏各种小东西。',
    trivia: '中国人使用黏土制作陶器已经有上万年的历史了。'
  },
  {
    id: 'limestone',
    name: '石灰石',
    description: '白色或灰白色的石头',
    category: CollectionCategory.MINERAL,
    rarity: Rarity.COMMON,
    emoji: '⬜',
    spawnChance: 0.2,
    value: 8,
    repeatable: true,
    lore: '可以用来刷墙，做石灰。',
    trivia: '石灰石烧制后变成生石灰，可以用来消毒和做建筑材料。'
  }
];

// 化石收集品
export const FOSSIL_COLLECTION: Collectible[] = [
  {
    id: 'ammonite',
    name: '菊石',
    description: '古代海洋生物的化石',
    category: CollectionCategory.FOSSIL,
    rarity: Rarity.RARE,
    emoji: '🐚',
    spawnChance: 0.05,
    value: 200,
    repeatable: false,
    lore: '在山上发现的化石，听说是很久很久以前海里生物变的。',
    trivia: '菊石是生活在几亿年前的海洋生物，和恐龙同时代灭绝。'
  },
  {
    id: 'trilobite',
    name: '三叶虫',
    description: '古老节肢动物的化石',
    category: CollectionCategory.FOSSIL,
    rarity: Rarity.EPIC,
    emoji: '🦀',
    spawnChance: 0.02,
    value: 500,
    repeatable: false,
    lore: '石头上的小虫子图案，听说是很古老的生物。',
    trivia: '三叶虫是寒武纪时期最常见的动物，已经灭绝了5亿年。'
  },
  {
    id: 'fish_fossil',
    name: '鱼化石',
    description: '古代鱼的化石',
    category: CollectionCategory.FOSSIL,
    rarity: Rarity.RARE,
    emoji: '🐟',
    spawnChance: 0.04,
    value: 250,
    repeatable: false,
    lore: '石头上有一条鱼的形状，非常逼真。',
    trivia: '鱼化石形成需要特殊的环境，通常是火山灰快速埋藏。'
  },
  {
    id: 'plant_fossil',
    name: '植物化石',
    description: '古代植物的印记',
    category: CollectionCategory.FOSSIL,
    rarity: Rarity.UNCOMMON,
    emoji: '🌿',
    spawnChance: 0.08,
    value: 100,
    repeatable: false,
    lore: '叶子的形状印在石头上，好看极了。',
    trivia: '植物化石可以告诉我们古代地球的气候和环境。'
  },
  {
    id: 'dinosaur_egg',
    name: '恐龙蛋',
    description: '远古恐龙下的蛋',
    category: CollectionCategory.FOSSIL,
    rarity: Rarity.LEGENDARY,
    emoji: '🥚',
    spawnChance: 0.005,
    value: 5000,
    repeatable: false,
    lore: '传说中的宝贝！据说能孵化出小恐龙。',
    trivia: '恐龙蛋化石非常稀有，是研究恐龙的重要证据。'
  }
];

// 手工收集品
export const CRAFT_COLLECTION: Collectible[] = [
  {
    id: 'bamboo_weaving',
    name: '竹编',
    description: '精美的手工竹编品',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.COMMON,
    emoji: '🎋',
    spawnChance: 0.15,
    value: 30,
    repeatable: true,
    lore: '李大叔编的竹篮，可结实了。',
    trivia: '竹编是中国传统手工艺，已经有几千年的历史。'
  },
  {
    id: 'paper_cutting',
    name: '剪纸',
    description: '精美的剪纸作品',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.COMMON,
    emoji: '✂️',
    spawnChance: 0.2,
    value: 20,
    repeatable: true,
    lore: '过年时窗户上贴的剪纸，好看极了。',
    trivia: '剪纸是中国民间艺术，已被列入非物质文化遗产。'
  },
  {
    id: 'embroidery',
    name: '刺绣',
    description: '手工刺绣的手帕',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.UNCOMMON,
    emoji: '🧵',
    spawnChance: 0.1,
    value: 80,
    repeatable: true,
    lore: '奶奶绣的手帕，上面有漂亮的花纹。',
    trivia: '中国刺绣有四大名绣：苏绣、湘绣、蜀绣、粤绣。'
  },
  {
    id: 'clay_figurine',
    name: '泥人',
    description: '用黏土捏的小人',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.COMMON,
    emoji: '🎎',
    spawnChance: 0.15,
    value: 25,
    repeatable: true,
    lore: '小时候玩的泥人，现在很少见了。',
    trivia: '泥人是中国传统玩具，可以用模具批量制作。'
  },
  {
    id: 'kite_handmade',
    name: '风筝',
    description: '手工制作的风筝',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.UNCOMMON,
    emoji: '🪁',
    unlockCondition: { season: 'spring', weather: 'sunny' },
    spawnChance: 0.1,
    value: 50,
    repeatable: true,
    lore: '爷爷做的风筝，飞得可高了！',
    trivia: '中国是风筝的发源地，已经有2000多年历史。'
  },
  {
    id: 'grass_grasshopper',
    name: '草蚱蜢',
    description: '用草编的蚱蜢',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.COMMON,
    emoji: '🦗',
    spawnChance: 0.2,
    value: 10,
    repeatable: true,
    lore: '用狗尾巴草编的蚱蜢，栩栩如生。',
    trivia: '草编是中国民间传统手工艺，取材天然。'
  },
  {
    id: 'lantern_handmade',
    name: '灯笼',
    description: '手工制作的纸灯笼',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.UNCOMMON,
    emoji: '🏮',
    unlockCondition: { season: 'winter' },
    spawnChance: 0.08,
    value: 60,
    repeatable: true,
    lore: '元宵节挂的灯笼，红彤彤的真好看。',
    trivia: '灯笼在中国传统文化中是吉祥的象征。'
  },
  {
    id: 'new_year_picture',
    name: '年画',
    description: '新年贴的年画',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.UNCOMMON,
    emoji: '🖼️',
    unlockCondition: { season: 'winter' },
    spawnChance: 0.1,
    value: 40,
    repeatable: true,
    lore: '过年时贴的年画，寓意吉祥如意。',
    trivia: '年画是中国传统文化的重要组成部分。'
  },
  {
    id: 'paper_crane_thousand',
    name: '千纸鹤',
    description: '折了上千只的纸鹤',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.RARE,
    emoji: '🦢',
    spawnChance: 0.05,
    value: 100,
    repeatable: false,
    lore: '折了999只千纸鹤，愿望就能实现。',
    trivia: '千纸鹤起源于日本，象征着和平与希望。'
  },
  {
    id: 'cloth_shoe',
    name: '布鞋',
    description: '奶奶做的千层底布鞋',
    category: CollectionCategory.CRAFT,
    rarity: Rarity.UNCOMMON,
    emoji: '👟',
    spawnChance: 0.08,
    value: 80,
    repeatable: false,
    lore: '奶奶一针一线纳的鞋底，穿着特别舒服。',
    trivia: '千层底布鞋是中国的传统手工艺，非常透气舒适。'
  }
];

// 所有收集品
export const ALL_COLLECTIONS: Collectible[] = [
  ...PLANT_COLLECTION,
  ...ITEM_COLLECTION,
  ...INSECT_COLLECTION,
  ...MINERAL_COLLECTION,
  ...FOSSIL_COLLECTION,
  ...CRAFT_COLLECTION
];

// 收集进度
export interface CollectionProgress {
  collectibleId: string;
  collected: boolean;
  collectCount: number;
  firstCollectTime: number;
}

// 收集系统管理器
export class CollectionSystem {
  private gameState: GameState;
  private floatingText?: FloatingTextSystem;
  
  // 收集进度
  private progress: Map<string, CollectionProgress> = new Map();
  
  // 今日可收集
  private todayCollectibles: string[] = [];
  private lastRefreshDay: number = 0;
  
  // 成就
  private achievements: Set<string> = new Set();
  
  constructor(gameState: GameState) {
    this.gameState = gameState;
    this.initProgress();
    this.refreshDailyCollectibles();
  }
  
  // 设置飘字系统
  setFloatingText(system: FloatingTextSystem): void {
    this.floatingText = system;
  }
  
  // 初始化进度
  private initProgress(): void {
    ALL_COLLECTIONS.forEach(item => {
      this.progress.set(item.id, {
        collectibleId: item.id,
        collected: false,
        collectCount: 0,
        firstCollectTime: 0
      });
    });
  }
  
  // 刷新每日可收集
  refreshDailyCollectibles(): void {
    const gameDay = Math.floor(Date.now() / (24 * 60 * 60 * 1000));
    if (gameDay === this.lastRefreshDay) return;
    
    this.lastRefreshDay = gameDay;
    this.todayCollectibles = [];
    
    // 随机选择今日可收集的物品
    ALL_COLLECTIONS.forEach(item => {
      if (Math.random() < item.spawnChance) {
        this.todayCollectibles.push(item.id);
      }
    });
  }
  
  // 尝试收集
  tryCollect(itemId: string): { success: boolean; collectible?: Collectible; firstTime: boolean } {
    const collectible = ALL_COLLECTIONS.find(c => c.id === itemId);
    if (!collectible) {
      return { success: false, firstTime: false };
    }
    
    // 检查今日是否可收集
    if (!this.todayCollectibles.includes(itemId)) {
      return { success: false, firstTime: false };
    }
    
    const progress = this.progress.get(itemId)!;
    const isFirstTime = !progress.collected;
    
    // 检查是否可重复收集
    if (progress.collected && !collectible.repeatable) {
      return { success: false, firstTime: false };
    }
    
    // 更新进度
    progress.collected = true;
    progress.collectCount++;
    if (isFirstTime) {
      progress.firstCollectTime = Date.now();
    }
    
    // 从今日可收集中移除
    const index = this.todayCollectibles.indexOf(itemId);
    if (index > -1) {
      this.todayCollectibles.splice(index, 1);
    }
    
    // 显示收集成功
    if (this.floatingText) {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 3;
      const rarityColor = this.getRarityColor(collectible.rarity);
      this.floatingText.spawn(centerX, centerY, `${collectible.emoji} 收集：${collectible.name}`, 'achievement');
    }
    
    // 检查成就
    this.checkAchievements();
    
    return { success: true, collectible, firstTime: isFirstTime };
  }
  
  // 随机收集
  randomCollect(): { success: boolean; collectible?: Collectible; firstTime: boolean } {
    if (this.todayCollectibles.length === 0) {
      return { success: false, firstTime: false };
    }
    
    const randomId = this.todayCollectibles[Math.floor(Math.random() * this.todayCollectibles.length)];
    return this.tryCollect(randomId);
  }
  
  // 获取稀有度颜色
  private getRarityColor(rarity: Rarity): string {
    switch (rarity) {
      case Rarity.COMMON: return '#FFFFFF';
      case Rarity.UNCOMMON: return '#00FF00';
      case Rarity.RARE: return '#0080FF';
      case Rarity.EPIC: return '#8000FF';
      case Rarity.LEGENDARY: return '#FFD700';
      default: return '#FFFFFF';
    }
  }
  
  // 检查成就
  private checkAchievements(): void {
    const collected = this.getCollectedCount();
    const total = ALL_COLLECTIONS.length;
    
    if (collected >= 1 && !this.achievements.has('first_collect')) {
      this.achievements.add('first_collect');
      this.floatingText?.spawn(window.innerWidth / 2, window.innerHeight / 2, '🏆 成就：初次收集', 'achievement');
    }
    
    if (collected >= total * 0.25 && !this.achievements.has('collector_25')) {
      this.achievements.add('collector_25');
      this.floatingText?.spawn(window.innerWidth / 2, window.innerHeight / 2, '🏆 成就：收集达人', 'achievement');
    }
    
    if (collected >= total * 0.5 && !this.achievements.has('collector_50')) {
      this.achievements.add('collector_50');
      this.floatingText?.spawn(window.innerWidth / 2, window.innerHeight / 2, '🏆 成就：收集专家', 'achievement');
    }
    
    if (collected >= total && !this.achievements.has('collector_complete')) {
      this.achievements.add('collector_complete');
      this.floatingText?.spawn(window.innerWidth / 2, window.innerHeight / 2, '🏆 成就：完美收集', 'achievement');
    }
    
    // 检查稀有收集
    const legendary = this.getCollectedByRarity(Rarity.LEGENDARY);
    if (legendary.length >= 1 && !this.achievements.has('legendary_collector')) {
      this.achievements.add('legendary_collector');
      this.floatingText?.spawn(window.innerWidth / 2, window.innerHeight / 2, '🏆 成就：传说收集者', 'achievement');
    }
  }
  
  // 获取收集统计
  getStats(): {
    total: number;
    collected: number;
    completion: number;
    byCategory: Record<CollectionCategory, { total: number; collected: number }>;
    byRarity: Record<Rarity, { total: number; collected: number }>;
  } {
    const result = {
      total: ALL_COLLECTIONS.length,
      collected: 0,
      completion: 0,
      byCategory: {} as Record<CollectionCategory, { total: number; collected: number }>,
      byRarity: {} as Record<Rarity, { total: number; collected: number }>
    };
    
    // 初始化分类统计
    Object.values(CollectionCategory).forEach(cat => {
      result.byCategory[cat] = { total: 0, collected: 0 };
    });
    Object.values(Rarity).forEach(rarity => {
      result.byRarity[rarity] = { total: 0, collected: 0 };
    });
    
    // 统计
    ALL_COLLECTIONS.forEach(item => {
      result.byCategory[item.category].total++;
      result.byRarity[item.rarity].total++;
      
      const progress = this.progress.get(item.id)!;
      if (progress.collected) {
        result.collected++;
        result.byCategory[item.category].collected++;
        result.byRarity[item.rarity].collected++;
      }
    });
    
    result.completion = result.collected / result.total;
    return result;
  }
  
  // 获取已收集数量
  getCollectedCount(): number {
    return Array.from(this.progress.values()).filter(p => p.collected).length;
  }
  
  // 按稀有度获取已收集
  getCollectedByRarity(rarity: Rarity): Collectible[] {
    return ALL_COLLECTIONS.filter(item => {
      if (item.rarity !== rarity) return false;
      const progress = this.progress.get(item.id)!;
      return progress.collected;
    });
  }
  
  // 按分类获取已收集
  getCollectedByCategory(category: CollectionCategory): Collectible[] {
    return ALL_COLLECTIONS.filter(item => {
      if (item.category !== category) return false;
      const progress = this.progress.get(item.id)!;
      return progress.collected;
    });
  }
  
  // 获取所有收集品（含进度）
  getAllCollectibles(): Array<Collectible & { collected: boolean; collectCount: number }> {
    return ALL_COLLECTIONS.map(item => {
      const progress = this.progress.get(item.id)!;
      return {
        ...item,
        collected: progress.collected,
        collectCount: progress.collectCount
      };
    });
  }
  
  // 获取图鉴详情
  getCollectibleDetail(id: string): (Collectible & CollectionProgress) | null {
    const collectible = ALL_COLLECTIONS.find(c => c.id === id);
    if (!collectible) return null;
    
    const progress = this.progress.get(id)!;
    return { ...collectible, ...progress };
  }
  
  // 获取今日可收集
  getTodayCollectibles(): Collectible[] {
    return this.todayCollectibles
      .map(id => ALL_COLLECTIONS.find(c => c.id === id)!)
      .filter(Boolean);
  }
  
  // 获取成就
  getAchievements(): string[] {
    return Array.from(this.achievements);
  }
  
  // 导出数据
  export(): object {
    return {
      progress: Object.fromEntries(this.progress),
      todayCollectibles: this.todayCollectibles,
      lastRefreshDay: this.lastRefreshDay,
      achievements: Array.from(this.achievements)
    };
  }
  
  // 导入数据
  import(data: any): void {
    if (data.progress) {
      this.progress = new Map(Object.entries(data.progress));
    }
    if (data.todayCollectibles) {
      this.todayCollectibles = data.todayCollectibles;
    }
    if (data.lastRefreshDay) {
      this.lastRefreshDay = data.lastRefreshDay;
    }
    if (data.achievements) {
      this.achievements = new Set(data.achievements);
    }
  }
}
